import { useMemo } from 'react'
import Select from '@/shared/components/Select/Select'
import { useTranslation } from 'react-i18next'
import Button from '@/shared/components/Button/Button'
import { useTableFilterStore } from '@/shared/components/Table/store/useTableFilterStore'
import { OptionType } from '@/shared/api/BaseAPI.model'

import {
  FILTER_KEYS,
  TABLE_KEYS
} from '@/shared/components/Table/models/Filter.model'
import { useSelectedLanguagesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useSelectedLanguagesQuery'
import { useMenuTypesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useMenuTypesQuery'
import { useDishSizesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishSizesQuery'
import { useDishCategoriesQuery } from '@/features/menu-management/SidebarAccordion/hooks/useDishCategoriesQuery'
import { useIngredientsQuery } from '@/features/menu-management/Ingredients/hooks/useIngredientsQuery'
import styles from './CustomFilterPanel.module.scss'
import { translatedName } from '@/shared/utils/translatedName'

interface CustomFilterPanelProps {
  filtersList: FILTER_KEYS[]
  tableKey: TABLE_KEYS
}

function CustomFilterPanel({
  filtersList = [],
  tableKey
}: CustomFilterPanelProps) {
  const { t } = useTranslation()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { selectedLanguagesOptions, defaultLanguageOption } =
    useSelectedLanguagesQuery(currentLanguage)

  const { menuTypesQuery } = useMenuTypesQuery()
  const { dishSizesList } = useDishSizesQuery()
  const { dishCategoriesList } = useDishCategoriesQuery()
  const { allergensList } = useIngredientsQuery()

  const { filters, setFilter, resetFilters } = useTableFilterStore()

  const tableFilters = filters[tableKey] ?? {}

  // Get the selected language from the filter, fallback to default
  const selectedFilterLanguage =
    tableFilters['language-filter']?.value || defaultLanguageOption?.value

  // Create options translated by the selected filter language
  const dishCategoriesOptions = useMemo(
    () =>
      dishCategoriesList.data?.map(category => ({
        label: translatedName(
          category.translations,
          selectedFilterLanguage as string | undefined,
          defaultLanguageOption?.value as string | undefined
        ),
        value: category.id
      })) || [],
    [
      dishCategoriesList.data,
      selectedFilterLanguage,
      defaultLanguageOption?.value
    ]
  ) as OptionType[]

  const dishSizesOptions = useMemo(
    () =>
      dishSizesList.data?.map(size => ({
        label: translatedName(
          size.translations,
          selectedFilterLanguage as string | undefined,
          defaultLanguageOption?.value as string | undefined
        ),
        value: size.id
      })) || [],
    [dishSizesList.data, selectedFilterLanguage, defaultLanguageOption?.value]
  ) as OptionType[]

  const menuTypesOptions = useMemo(
    () =>
      menuTypesQuery.data?.map(type => ({
        label: translatedName(
          type.translations,
          selectedFilterLanguage as string | undefined,
          defaultLanguageOption?.value as string | undefined
        ),
        value: type.id
      })) || [],
    [menuTypesQuery.data, selectedFilterLanguage, defaultLanguageOption?.value]
  ) as OptionType[]

  const allergensOptions = useMemo(
    () =>
      allergensList.data?.map(allergen => ({
        value: allergen.id,
        label: translatedName(
          allergen.translations,
          selectedFilterLanguage as string | undefined,
          defaultLanguageOption?.value as string | undefined
        ),
        iconImage: allergen.iconUrl
      })) || [],
    [allergensList.data, selectedFilterLanguage, defaultLanguageOption?.value]
  )

  // Helper to translate selected values' labels based on current language
  const getTranslatedValues = (
    storedValues: OptionType[] | undefined,
    options: OptionType[]
  ): OptionType[] => {
    if (!storedValues || storedValues.length === 0) return []
    return storedValues
      .map(stored => options.find(opt => opt.value === stored.value))
      .filter((opt): opt is OptionType => opt !== undefined)
  }

  // Translate selected filter values to current language
  const translatedCategoriesValue = useMemo(
    () =>
      getTranslatedValues(
        tableFilters['categories-filter'],
        dishCategoriesOptions
      ),
    [tableFilters, dishCategoriesOptions]
  )

  const translatedDishSizesValue = useMemo(
    () =>
      getTranslatedValues(tableFilters['dish-size-filter'], dishSizesOptions),
    [tableFilters, dishSizesOptions]
  )

  const translatedMenuTypesValue = useMemo(
    () =>
      getTranslatedValues(tableFilters['menu-type-filter'], menuTypesOptions),
    [tableFilters, menuTypesOptions]
  )

  const translatedAllergensValue = useMemo(
    () =>
      getTranslatedValues(
        tableFilters[FILTER_KEYS.ALLERGENS_FILTER],
        allergensOptions
      ),
    [tableFilters, allergensOptions]
  )

  return (
    <div className={styles.tableCustomFilterWrapper}>
      <p className={`${styles.tableCustomFilterTitle} body-bold-medium`}>
        {t('TABLE.FILTER.TITLE')}
      </p>
      <p className={`${styles.tableCustomFilterDescription} body-medium`}>
        {t('TABLE.FILTER.DESCRIPTION')}
      </p>
      <div className={styles.tableCustomFilterSelectContainer}>
        {filtersList?.includes(FILTER_KEYS.LANGUANGE_FILTER) && (
          <Select
            key={FILTER_KEYS.LANGUANGE_FILTER}
            options={selectedLanguagesOptions}
            placeholder={t('FILTER.SELECT_LANGUAGE')}
            label={t('FILTER.LANGUAGE')}
            focused
            value={tableFilters['language-filter'] ?? defaultLanguageOption}
            onChange={val =>
              setFilter(tableKey, FILTER_KEYS.LANGUANGE_FILTER, val)
            }
          />
        )}
        {filtersList?.includes(FILTER_KEYS.CATEGORIES_FILTER) && (
          <Select
            key={FILTER_KEYS.CATEGORIES_FILTER}
            options={dishCategoriesOptions}
            placeholder={t('FILTER.SELECT_CATEGORIES')}
            label={t('FILTER.CATEGORIES')}
            isMutiSelect
            focused
            value={translatedCategoriesValue}
            onChange={val =>
              setFilter(tableKey, FILTER_KEYS.CATEGORIES_FILTER, val)
            }
          />
        )}
        {filtersList?.includes(FILTER_KEYS.DISH_SIZE_FILTER) && (
          <Select
            key={FILTER_KEYS.DISH_SIZE_FILTER}
            options={dishSizesOptions}
            placeholder={t('FILTER.SELECT_DISH_SIZES')}
            label={t('FILTER.DISH_SIZES')}
            isMutiSelect
            focused
            value={translatedDishSizesValue}
            onChange={val =>
              setFilter(tableKey, FILTER_KEYS.DISH_SIZE_FILTER, val)
            }
          />
        )}
        {filtersList?.includes(FILTER_KEYS.MENU_TYPE_FILTER) && (
          <Select
            key={FILTER_KEYS.MENU_TYPE_FILTER}
            options={menuTypesOptions}
            placeholder={t('FILTER.SELECT_MENU_TYPES')}
            label={t('FILTER.MENU_TYPES')}
            isMutiSelect
            focused
            value={translatedMenuTypesValue}
            onChange={val =>
              setFilter(tableKey, FILTER_KEYS.MENU_TYPE_FILTER, val)
            }
          />
        )}
        {filtersList?.includes(FILTER_KEYS.ALLERGENS_FILTER) && (
          <Select
            key={FILTER_KEYS.ALLERGENS_FILTER}
            options={allergensOptions}
            placeholder={t('DETAILS_TILE.MENU.ALLERGENS.PLACEHOLDER')}
            label={t('DETAILS_TILE.MENU.ALLERGENS.LABEL')}
            isMutiSelect
            focused
            value={translatedAllergensValue}
            onChange={val =>
              setFilter(tableKey, FILTER_KEYS.ALLERGENS_FILTER, val)
            }
          />
        )}
      </div>
      <div className={styles.tableCustomFilterPanelContainer}>
        <Button
          label={t('FILTER.CLEAR_FILTERS')}
          variant="text"
          color="primary"
          onClick={() => resetFilters(tableKey, defaultLanguageOption)}
        />
      </div>
    </div>
  )
}

export default CustomFilterPanel
